import { View, StyleSheet, Keyboard } from 'react-native';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { selectMergedCustomers, type TopCustomer } from '../../redux/OrdersSlice';
import { GTFonts, useTheme } from '../../theme';
import Button from '../Reusables/Button';
import { ModalContext } from '../../Context/modalContext';
import { s, fs, v } from '../../utils/libs';
import Typography from '../Reusables/Typography';
import CustomBottomSheet from '../Reusables/bottomSheet/CustomBottomSheet';
import OrderCustomerStaffSection from './OrderCustomerStaffSection';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  getCachedLocationAssignableStaff,
  getLocationAssignableStaff,
  type AssignableStaffMember,
} from '../../api/employees';
import { isEmployeeAdminRole, isEmployeeWaiterRole } from '../../utils/roles';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  createCustomer,
  parseCustomerResponse,
} from '../../api/backofficeCustomers';
import { addCustomer } from '../../redux/backoffice/customersSlice';
import { AppSnackbar } from '../../services/AppSnackbar';
import { getApiMessage } from '../../utils/apiError';

type StaffMember = AssignableStaffMember;

const SaveOrderBottomSheet = ({
  SendOrder,
  isloading,
}: {
  SendOrder: () => void;
  isloading: boolean;
}) => {
  const {
    visibleName,
    setVisibleName,
    input,
    setInput,
    assignedStaffId,
    setAssignedStaffId,
    selectedCustomerId,
    setSelectedCustomerId,
  } = useContext(ModalContext);
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const sheetRef = useRef<BottomSheetModal | null>(null);
  const dispatch = useAppDispatch();
  const { auth } = useAuth();
  const axiosprivate = useAxiosPrivate();
  const { owner, autoLoginOwner } = useBackofficeOwnerAuth();
  const topCustomers = useAppSelector(selectMergedCustomers);
  const currentLocation = useAppSelector(state => state.location.currentLocation);
  const allowStaffOrderAssignmentSetting = useAppSelector(
    state => state.settings.settings.allow_staff_order_assignment,
  );
  const employeeRole = useAppSelector(
    state => state.auth.inApp.account.employeeInfo?.bp_employee_role,
  );
  const isWaiter = isEmployeeWaiterRole(employeeRole);
  const isAdmin = isEmployeeAdminRole(employeeRole);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [isNewCustomerFormOpen, setIsNewCustomerFormOpen] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(true);
  const [staffQuery, setStaffQuery] = useState('');
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [staffEndpointFeatureDisabled, setStaffEndpointFeatureDisabled] =
    useState(false);
  const locationId = String(currentLocation?._id || '');

  const isStaffAssignmentEnabled = useMemo(() => {
    if (isWaiter) return false;
    const fromLocation =
      currentLocation?.bp_location_settings?.bp_location_allow_staff_order_assignment;
    if (typeof fromLocation === 'boolean') return fromLocation;
    return Boolean(allowStaffOrderAssignmentSetting);
  }, [
    isWaiter,
    currentLocation?.bp_location_settings?.bp_location_allow_staff_order_assignment,
    allowStaffOrderAssignmentSetting,
  ]);

  const closeSheet = useCallback(() => {
    setVisibleName(false);
    Keyboard.dismiss();
  }, [setVisibleName]);

  const filteredCustomers = useMemo<TopCustomer[]>(() => {
    if (!input || input.trim().length === 0) {
      return topCustomers as TopCustomer[];
    }

    const searchTerm = input.trim().toLowerCase();

    const matches = (topCustomers as TopCustomer[]).filter(customer =>
      customer.name.toLowerCase().includes(searchTerm),
    );

    return matches.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      if (aName === searchTerm && bName !== searchTerm) return -1;
      if (bName === searchTerm && aName !== searchTerm) return 1;

      const aStartsWith = aName.startsWith(searchTerm);
      const bStartsWith = bName.startsWith(searchTerm);
      if (aStartsWith && !bStartsWith) return -1;
      if (bStartsWith && !aStartsWith) return 1;

      return (b.count || 0) - (a.count || 0);
    });
  }, [topCustomers, input]);

  const filteredStaff = useMemo(() => {
    const query = staffQuery.trim().toLowerCase();
    if (!query) {
      return staffMembers;
    }

    return staffMembers.filter(staff => {
      const nameMatch = staff.name.toLowerCase().includes(query);
      const roleMatch = staff.role.toLowerCase().includes(query);
      return nameMatch || roleMatch;
    });
  }, [staffQuery, staffMembers]);

  const shouldShowCustomerDropdown =
    showCustomerDropdown && filteredCustomers.length > 0 && !isloading;

  const canSaveOrder = input.trim().length > 0 && !isloading;

  const handleCreateCustomer = useCallback(
    async (data: { name: string; phone: string; email: string }) => {
      if (!locationId) {
        AppSnackbar.show('No location selected', 'error');
        return;
      }
      try {
        setIsCreatingCustomer(true);

        let accessToken = owner?.accessToken;
        if (!accessToken) {
          const loginResult = await autoLoginOwner();
          if (loginResult.status === 'error' || !('accessToken' in loginResult)) {
            AppSnackbar.show('Owner authentication failed', 'error');
            return;
          }
          accessToken = loginResult.accessToken;
        }

        const res = await createCustomer(
          axiosprivate as any,
          locationId,
          {
            bp_customer_name: data.name,
            bp_customer_phone_number: data.phone,
            ...(data.email ? { bp_customer_email_address: data.email } : {}),
          },
          { accessToken },
        );
        const ok = res?.data?.success === true || res?.status === 201;
        if (!ok) {
          AppSnackbar.show(
            String(res?.data?.message || 'Failed to add customer'),
            'error',
          );
          return;
        }
        const parsed = parseCustomerResponse(res);
        const created = parsed?.customer || res?.data?.customer;
        if (created?._id) {
          dispatch(addCustomer(created));
        }
        setInput(data.name);
        setSelectedCustomerId(created?._id || null);
        setShowCustomerDropdown(false);
        Keyboard.dismiss();
        AppSnackbar.show('Customer added', 'success');
      } catch (e: any) {
        AppSnackbar.show(getApiMessage(e, 'Failed to add customer'), 'error');
      } finally {
        setIsCreatingCustomer(false);
      }
    },
    [
      axiosprivate,
      autoLoginOwner,
      dispatch,
      locationId,
      owner?.accessToken,
      setInput,
      setSelectedCustomerId,
    ],
  );

  const handleSubmitName = useCallback(() => {
    if (canSaveOrder) {
      SendOrder();
      return;
    }
    Keyboard.dismiss();
  }, [SendOrder, canSaveOrder]);

  const handleToggleStaff = useCallback(
    (id: string) => {
      setAssignedStaffId(prev => (prev === id ? null : id));
    },
    [setAssignedStaffId],
  );

  useEffect(() => {
    if (!visibleName) {
      Keyboard.dismiss();
      setStaffQuery('');
      setAssignedStaffId(null);
      setSelectedCustomerId(null);
      setIsNewCustomerFormOpen(false);
      return;
    }

    setShowCustomerDropdown(true);
  }, [visibleName, setAssignedStaffId, setSelectedCustomerId]);

  useEffect(() => {
    if (visibleName) {
      sheetRef.current?.present();
      return;
    }
    sheetRef.current?.dismiss();
  }, [visibleName]);

  useEffect(() => {
    if (!visibleName || !isStaffAssignmentEnabled || !locationId) return;

    const cached = getCachedLocationAssignableStaff(locationId);
    if (cached) {
      setStaffMembers(cached.staff);
      setStaffEndpointFeatureDisabled(cached.featureDisabled);
      if (cached.staff.length > 0) {
        return;
      }
    }

    if (!auth?.accessToken) return;

    let active = true;
    const loadAssignableStaff = async () => {
      try {
        setIsLoadingStaff(true);
        const result = await getLocationAssignableStaff(
          axiosprivate as any,
          auth,
          locationId,
        );
        if (!active) return;
        setStaffMembers(result.data);
        setStaffEndpointFeatureDisabled(result.status === 'disabled');
      } catch (error) {
        if (!active) return;
        setStaffEndpointFeatureDisabled(false);
      } finally {
        if (active) setIsLoadingStaff(false);
      }
    };

    void loadAssignableStaff();
    return () => {
      active = false;
    };
  }, [
    visibleName,
    isStaffAssignmentEnabled,
    locationId,
    axiosprivate,
    auth,
    auth?.accessToken,
  ]);

  const shouldShowStaffSection =
    isStaffAssignmentEnabled && !staffEndpointFeatureDisabled;

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index !== -1) return;
      if (isloading) {
        sheetRef.current?.present();
        return;
      }
      closeSheet();
    },
    [closeSheet, isloading],
  );

  return (
    <CustomBottomSheet
      bottomSheetModalRef={sheetRef}
      snapPoints={['80%']}
      constrainWidth={true}
      enableDynamicSizing={false}
      handleSheetChanges={handleSheetChanges}
      header={
        <View style={styles.headerRow}>
          <Typography style={styles.title}>Save Order</Typography>
        </View>
      }
      footer={
        isNewCustomerFormOpen ? undefined : (
          <View style={styles.saveBtnWrap}>
            <Button
              text="Save Order"
              onPress={() => {
                if (canSaveOrder) {
                  SendOrder();
                }
              }}
              disabled={!canSaveOrder}
              loading={isloading}
              backgroundColor={
                canSaveOrder ? theme.colors.successDark : theme.colors.borderGray200
              }
              rippleColor={theme.colors.success}
              borderRadius={s(theme.borderRadius.md)}
              height={v(46)}
              fontSize={fs(14)}
              fullWidth
            />
          </View>
        )
      }>
      <View style={styles.content}>
        <OrderCustomerStaffSection
          input={input}
          isLoadingOrder={isloading}
          showCustomerDropdown={shouldShowCustomerDropdown}
          filteredCustomers={filteredCustomers}
          setInput={text => {
            setInput(text);
            setSelectedCustomerId(null);
          }}
          onCustomerInputFocus={() => setShowCustomerDropdown(true)}
          onSubmitName={handleSubmitName}
          onSelectCustomer={(name, customerId) => {
            setInput(name);
            setSelectedCustomerId(customerId ?? null);
            setShowCustomerDropdown(false);
            Keyboard.dismiss();
          }}
          shouldShowStaffSection={shouldShowStaffSection}
          staffQuery={staffQuery}
          setStaffQuery={setStaffQuery}
          isLoadingStaff={isLoadingStaff}
          filteredStaff={filteredStaff}
          assignedStaffId={assignedStaffId}
          toggleStaffSelection={handleToggleStaff}
          showAddNewCustomerChip={isAdmin}
          onCreateCustomer={handleCreateCustomer}
          isCreatingCustomer={isCreatingCustomer}
          onNewFormVisibilityChange={setIsNewCustomerFormOpen}
        />
      </View>
    </CustomBottomSheet>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    headerRow: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: v(8),
      paddingBottom: v(12),
    },
    title: {
      fontSize: fs(20),
      lineHeight: fs(22),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    content: {
      paddingHorizontal: s(10),
      // paddingBottom: v(4),
      backgroundColor: theme.colors.surface,
    },
    saveBtnWrap: {
      marginTop: v(4),
      marginHorizontal: s(10),
    },
  });

export default SaveOrderBottomSheet;
